import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'

function PasswordInput({
  value,
  onChange,
  placeholder,
  show,
  onToggleShow,
}: {
  value: string
  onChange: (v: string) => void
  placeholder: string
  show: boolean
  onToggleShow: () => void
}) {
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />
      <button
        type="button"
        onClick={onToggleShow}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  )
}

export default function AccountSettingsPage() {
  const user = useAuthStore((s) => s.user)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handlePasswordChange() {
    setError(null)

    if (!currentPassword) {
      setError('Masukkan password saat ini.')
      return
    }
    if (newPassword.length < 8) {
      setError('Password baru minimal 8 karakter.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Password baru dan konfirmasi tidak cocok.')
      return
    }
    if (currentPassword === newPassword) {
      setError('Password baru harus berbeda dari password saat ini.')
      return
    }

    setSaving(true)

    // Verify current password by re-authenticating
    const { error: verifyError } = await supabase.auth.signInWithPassword({
      email: user?.email ?? '',
      password: currentPassword,
    })

    if (verifyError) {
      setSaving(false)
      setError('Password saat ini tidak sesuai.')
      return
    }

    // Current password verified — update to new password
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword })
    setSaving(false)

    if (updateError) {
      setError(updateError.message)
    } else {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Akun</h1>
        <p className="text-muted-foreground">Kelola informasi akun dan keamanan login Anda.</p>
      </div>

      {/* Info akun */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-3">
        <h2 className="font-semibold">Informasi Akun</h2>
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Email</p>
          <p className="text-sm font-medium">{user?.email ?? '—'}</p>
        </div>
      </div>

      {/* Ganti password */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div>
          <h2 className="font-semibold">Ganti Password</h2>
          <p className="text-sm text-muted-foreground">Ubah password login dashboard Anda.</p>
        </div>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">Password Saat Ini</label>
            <PasswordInput
              value={currentPassword}
              onChange={setCurrentPassword}
              placeholder="Password yang sekarang digunakan"
              show={showCurrent}
              onToggleShow={() => setShowCurrent((v) => !v)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Password Baru</label>
            <PasswordInput
              value={newPassword}
              onChange={setNewPassword}
              placeholder="Minimal 8 karakter"
              show={showNew}
              onToggleShow={() => setShowNew((v) => !v)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Konfirmasi Password Baru</label>
            <PasswordInput
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="Ulangi password baru"
              show={showConfirm}
              onToggleShow={() => setShowConfirm((v) => !v)}
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handlePasswordChange}
            disabled={saving || !currentPassword || !newPassword || !confirmPassword}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? 'Menyimpan...' : 'Ganti Password'}
          </button>
          {saved && <p className="text-sm text-green-600">Password berhasil diganti!</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      </div>
    </div>
  )
}
